import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Clock,
  PlayCircle,
  RefreshCw,
  Calendar,
  TrendingUp,
  Settings,
  Save,
  Sparkles,
} from "lucide-react";

const JOB_LABELS = {
  followup_daily: {
    label: "Daily Follow-up Dispatch",
    tone: "bg-purple-100 text-purple-700",
    cron: "08:00 UTC daily",
  },
  mgmt_review: {
    label: "Management Review Digest",
    tone: "bg-indigo-100 text-indigo-700",
    cron: "07:30 UTC daily check (fires per tenant cadence)",
  },
};

const CADENCE_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annual", label: "Semi-Annual" },
  { value: "annual", label: "Annual" },
];

const DOW_OPTIONS = [
  { value: "1", label: "Mon" },
  { value: "2", label: "Tue" },
  { value: "3", label: "Wed" },
  { value: "4", label: "Thu" },
  { value: "5", label: "Fri" },
  { value: "6", label: "Sat" },
  { value: "7", label: "Sun" },
];

export default function SchedulerAdminPage() {
  const { api, user } = useAuth();
  const [data, setData] = useState({ count: 0, total_dispatched: 0, by_job: {}, runs: [] });
  const [loading, setLoading] = useState(true);

  // Management-review config state
  const [cfg, setCfg] = useState({
    cadence: "quarterly",
    day_of_week: 1,
    day_of_month: 1,
    enabled: true,
  });
  const [cfgLoading, setCfgLoading] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ai-v14/scheduler/runs?limit=50");
      setData(r.data || {});
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not load scheduler history"));
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    setCfgLoading(true);
    try {
      const r = await api.get("/ai-v14/mgmt-review/config");
      setCfg({
        cadence: r.data?.cadence || "quarterly",
        day_of_week: r.data?.day_of_week || 1,
        day_of_month: r.data?.day_of_month || 1,
        enabled: r.data?.enabled !== false,
      });
    } catch (e) {
      // non-fatal
    } finally {
      setCfgLoading(false);
    }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await api.put("/ai-v14/mgmt-review/config", {
        cadence: cfg.cadence,
        day_of_week: parseInt(cfg.day_of_week, 10),
        day_of_month: parseInt(cfg.day_of_month, 10),
        enabled: !!cfg.enabled,
      });
      toast.success("Management Review schedule saved");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save schedule"));
    } finally {
      setSavingCfg(false);
    }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const r = await api.post("/ai-v14/mgmt-review/trigger");
      toast.success(
        `Management Review triggered (${r.data?.dispatched ?? 0} recipients) — period ${r.data?.period_key}`
      );
      loadHistory();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not trigger review"));
    } finally {
      setRunningNow(false);
    }
  };

  useEffect(() => {
    loadHistory();
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canView = ["bcm_manager", "admin"].includes(user?.role);
  if (!canView) {
    return (
      <div className="p-12 text-center">
        <p className="text-slate-600">BCM Manager or admin access required.</p>
      </div>
    );
  }

  const formatTs = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6" data-testid="scheduler-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Settings className="w-7 h-7 text-slate-600" />
            Background Scheduler
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            V14 APScheduler — follow-up dispatch &amp; Management Review digest (ISO 22301 §9.3).
          </p>
        </div>
        <Button onClick={loadHistory} variant="outline" disabled={loading} data-testid="refresh-scheduler-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">Total Runs</p>
            <p className="text-2xl font-extrabold text-slate-800 mt-1" data-testid="total-runs">
              {data.count || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">Reminders Dispatched</p>
            <p className="text-2xl font-extrabold text-emerald-700 mt-1" data-testid="total-dispatched">
              {data.total_dispatched || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">Active Jobs</p>
            <p className="text-2xl font-extrabold text-slate-800 mt-1" data-testid="active-jobs">
              {Object.keys(JOB_LABELS).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Management Review Schedule Config */}
      <Card className="border-indigo-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            Management Review Schedule
            <Badge variant="outline" className="ml-2 text-[10px] border-indigo-300 text-indigo-700">
              ISO 22301 §9.3
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-4">
            Set the cadence for the automated Management Review digest. BCM Managers and Department Heads
            receive a notification summarising open BIAs, approvals, exercises and incidents on each scheduled date.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Cadence</Label>
              <Select value={cfg.cadence} onValueChange={(v) => setCfg({ ...cfg, cadence: v })}>
                <SelectTrigger data-testid="mgmt-review-cadence" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CADENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {cfg.cadence === "weekly" ? (
              <div>
                <Label className="text-xs">Day of Week</Label>
                <Select
                  value={String(cfg.day_of_week)}
                  onValueChange={(v) => setCfg({ ...cfg, day_of_week: parseInt(v, 10) })}
                >
                  <SelectTrigger data-testid="mgmt-review-dow" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOW_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-xs">Day of Month (1–28)</Label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={cfg.day_of_month}
                  onChange={(e) => setCfg({ ...cfg, day_of_month: parseInt(e.target.value, 10) || 1 })}
                  className="mt-1"
                  data-testid="mgmt-review-dom"
                />
              </div>
            )}
            <div className="flex items-center gap-3 mt-5">
              <Switch
                checked={cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                data-testid="mgmt-review-enabled"
              />
              <Label className="text-sm">Enabled</Label>
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={saveConfig}
                disabled={savingCfg || cfgLoading}
                data-testid="save-mgmt-review-btn"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingCfg ? "Saving…" : "Save"}
              </Button>
              <Button
                onClick={runNow}
                disabled={runningNow}
                variant="outline"
                data-testid="trigger-mgmt-review-btn"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                {runningNow ? "Running…" : "Run Now"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Registered Jobs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(JOB_LABELS).map(([key, meta]) => (
              <div
                key={key}
                className="flex items-center justify-between border border-slate-200 rounded-lg p-3"
                data-testid={`job-${key}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.tone}`}>
                    <PlayCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{meta.label}</p>
                    <p className="text-xs text-slate-500">{meta.cron}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    Runs: {data.by_job?.[key] || 0}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Recent Run History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse h-24 bg-slate-100 rounded-lg" />
          ) : (data.runs || []).length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No scheduler runs yet.</p>
              <p className="text-xs text-slate-400 mt-1">
                Daily jobs run at 07:30 UTC (Management Review check) and 08:00 UTC (Follow-up Dispatch).
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="runs-table">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 font-semibold">Job</th>
                    <th className="text-left py-2 font-semibold">Run Date</th>
                    <th className="text-left py-2 font-semibold">Executed</th>
                    <th className="text-right py-2 font-semibold">Dispatched</th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.map((r, i) => {
                    const meta = JOB_LABELS[r.job] || { label: r.job, tone: "bg-slate-100 text-slate-700" };
                    return (
                      <tr key={i} className="border-b border-slate-100 last:border-0" data-testid={`run-row-${i}`}>
                        <td className="py-2.5">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${meta.tone}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-2.5 text-slate-600">{r.run_date || "—"}</td>
                        <td className="py-2.5 text-slate-500 text-[12px]">{formatTs(r.executed_at)}</td>
                        <td className="py-2.5 text-right font-semibold text-emerald-700">
                          {r.dispatched ?? 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
